/** @jsxImportSource solid-js */
import CorvuDialog from '@corvu/dialog'
import { Show, type JSX } from 'solid-js'
import { cn } from '../cn'

export interface DialogShellProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Whether Escape requests dismissal. Defaults to the corvu behavior. */
  closeOnEscapeKeyDown?: boolean
  /** Whether focus returns to the pre-dialog element when the shell closes. */
  restoreFocus?: boolean
  /** Reports Corvu content presence, including pending exit animations. */
  onContentPresentChange?: (present: boolean) => void
  /** ARIA role of the panel; alert flows pass 'alertdialog'. */
  role?: 'dialog' | 'alertdialog'
  /** Extra classes for the glass panel (width, z-index, animations, …). */
  class?: string
  /** Stable selector for integration tests and host diagnostics. */
  testId?: string
  /** The panel element, for composers that watch its images or animations. */
  ref?: (panel: HTMLElement) => void
  /** Extra classes for the overlay (z-index, animations, …). */
  overlayClass?: string
  /** Optional content positioned by the composer above the overlay. */
  overlayContent?: JSX.Element
  children: JSX.Element
}

/**
 * The Forest glass dialog frame: centered panel with gradient outline and
 * blurred overlay — the corvu twin of `src/react/dialog-shell.tsx`. Outside
 * taps never dismiss; dismissal goes through `onOpenChange(false)` (Escape),
 * so composers gate it there (e.g. while a mutation is in flight).
 */
export function DialogShell(props: DialogShellProps) {
  return (
    <CorvuDialog
      role={props.role}
      open={props.open}
      onOpenChange={props.onOpenChange}
      closeOnEscapeKeyDown={props.closeOnEscapeKeyDown}
      restoreFocus={props.restoreFocus}
      onContentPresentChange={props.onContentPresentChange}
      closeOnOutsidePointer={false}
      closeOnOutsideFocus={false}
    >
      <CorvuDialog.Portal>
        <CorvuDialog.Overlay
          class={cn(
            'fixed inset-0 z-[70] bg-black/10 backdrop-blur-lg',
            props.overlayClass,
          )}
        />
        <Show when={props.open}>{props.overlayContent}</Show>
        <CorvuDialog.Content
          ref={props.ref}
          data-testid={props.open ? props.testId : undefined}
          class={cn(
            // inset-0 + m-auto centering instead of the React shell's
            // translate approach — it composes with tailwindcss-animate's
            // animate-in/out without the --tw-enter-translate-* workaround.
            'fixed inset-0 z-[70] m-auto h-fit w-fit',
            'rounded-[32px] bg-white/80 backdrop-blur-[50px] will-change-[backdrop-filter]',
            // Gradient outline drawn by a masked ::before ring (the
            // .gradient-outline effect from shop/referral, inlined so
            // consumers need no extra CSS).
            'before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:p-[2px]',
            'before:[background:linear-gradient(90deg,#ffffff4d,#ffffff1a)]',
            'before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)]',
            'before:[-webkit-mask-composite:xor]',
            'before:[mask-composite:exclude]',
            props.class,
          )}
        >
          {props.children}
        </CorvuDialog.Content>
      </CorvuDialog.Portal>
    </CorvuDialog>
  )
}
